import React, { useState, useMemo, useEffect } from 'react';
import { Award, ChevronDown, ChevronUp, GraduationCap, AlertTriangle } from 'lucide-react';
import { useMergedCertificates } from '../../../../hooks/domain/crew/useCrewCertificates';
import {
  processCertificates,
  formatCertificateDate,
  formatExpiryText,
  getStatusDisplay,
  truncateCertificateName,
  getCertificateDisplayName
} from '../../../../utils/crew/certificateUtils';

/**
 * CertificatesSummaryCard Component
 * Displays crew certificates with progressive disclosure
 * Shows stats grid (always visible) and categorized list (expandable)
 *
 * @param {string} crewId - Crew member ID
 */
export default function CertificatesSummaryCard({ crewId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get merged certificates from domain hook
  const certificates = useMergedCertificates(crewId);

  // Process certificates into categories and stats
  const { categories, stats, expiredCerts } = useMemo(
    () => processCertificates(certificates),
    [certificates]
  );

  // Toggle category expansion (mobile only)
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Empty state
  if (stats.total === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Certificates</h3>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-gray-500 italic text-sm">No certificates on record</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - Inside Card */}
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Certificates</h3>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
            {stats.total}
          </span>
        </div>
      </div>

      <div className="px-6 py-5">
        {/* Traffic Light Status Summary */}
        <div className="space-y-3">
          {/* Overall Compliance */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Certificate Compliance</span>
            <span className="text-sm font-semibold text-gray-900">
              {stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0}%
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300 bg-green-600"
              style={{ width: `${stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0}%` }}
            />
          </div>

          {/* Status Breakdown with Traffic Light Dots */}
          <div className="space-y-2 pt-2">
            {/* Expired - Red */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Expired</span>
              </div>
              <span className="text-sm font-semibold text-red-600">{stats.expired}</span>
            </div>

            {/* Expiring Soon - Amber */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Expiring Soon</span>
              </div>
              <span className="text-sm font-semibold text-amber-600">{stats.expiringSoon}</span>
            </div>

            {/* Valid - Green */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Valid</span>
              </div>
              <span className="text-sm font-semibold text-green-600">{stats.valid}</span>
            </div>
          </div>

          {/* Total */}
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-sm font-semibold text-gray-900">{stats.total} certificates</span>
            </div>
          </div>

          {/* Warning Alert - if expired > 0 */}
          {expiredCerts.length > 0 && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {expiredCerts.length} certificate{expiredCerts.length !== 1 ? 's' : ''} expired
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show more
            </>
          )}
        </button>

        {/* Expanded Content - Categories */}
        {isExpanded && (
          <div className="mt-4 space-y-3">
            {Object.values(categories).map(category => (
              <CategorySection
                key={category.id}
                category={category}
                isExpanded={expandedCategories[category.id]}
                onToggle={() => toggleCategory(category.id)}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CategorySection Component
 * Displays a category of certificates with collapsible list (mobile)
 */
function CategorySection({ category, isExpanded, onToggle, isMobile }) {
  // Desktop: always expanded, Mobile: collapsible
  const shouldShowContent = !isMobile || isExpanded;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Category Header */}
      <button
        onClick={isMobile ? onToggle : undefined}
        className={`w-full flex items-center justify-between p-3 bg-gray-50 ${
          isMobile ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
        }`}
        disabled={!isMobile}
        type="button"
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">{category.icon}</span>
          <div className="text-left">
            <h4 className="text-sm font-semibold text-gray-900">
              {category.label}
            </h4>
            <p className="text-xs text-gray-500">
              {category.count} certificate{category.count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Status Indicators */}
          {category.hasExpired && (
            <span className="text-red-600 text-sm">❌</span>
          )}
          {category.hasExpiringSoon && !category.hasExpired && (
            <span className="text-yellow-600 text-sm">⚠️</span>
          )}

          {/* Chevron (mobile only) */}
          {isMobile && (
            isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )
          )}
        </div>
      </button>

      {/* Certificate List */}
      {shouldShowContent && (
        <div className="divide-y divide-gray-100">
          {category.certificates.map((cert, index) => (
            <CertificateItem key={cert.id || index} certificate={cert} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * CertificateItem Component
 * Displays individual certificate with status badge
 */
function CertificateItem({ certificate, index }) {
  const statusDisplay = getStatusDisplay(certificate.expiryStatus);
  const certName = getCertificateDisplayName(certificate, index);

  return (
    <div className="p-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-2">
        {/* Certificate Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h5 className="text-sm font-medium text-gray-900 truncate">
              {truncateCertificateName(certName)}
            </h5>
            {/* Training Badge */}
            {(certificate.isFromTraining || certificate.source === 'training') && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                <GraduationCap className="h-3 w-3 mr-0.5" />
                Training
              </span>
            )}
          </div>

          {/* Expiry Text */}
          <span className={`text-xs ${statusDisplay.textColor}`}>
            {formatExpiryText(certificate.expiry_date || certificate.expiryDate)}
          </span>

          {/* Issue/Expiry Dates */}
          <div className="text-xs text-gray-500 mt-1">
            {(certificate.issue_date || certificate.issueDate) && (
              <span>Issued: {formatCertificateDate(certificate.issue_date || certificate.issueDate)}</span>
            )}
            {(certificate.issue_date || certificate.issueDate) && (certificate.expiry_date || certificate.expiryDate) && (
              <span className="mx-1">•</span>
            )}
            {(certificate.expiry_date || certificate.expiryDate) && (
              <span>Expires: {formatCertificateDate(certificate.expiry_date || certificate.expiryDate)}</span>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor} border ${statusDisplay.borderColor}`}>
          <span className="mr-1">{statusDisplay.icon}</span>
          {statusDisplay.label}
        </div>
      </div>
    </div>
  );
}
